import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Icon } from "@iconify/react";
import {
  getGruposThunk,
  getUsuariosPermisosThunk,
} from "../../store/rolesPermisos/rolesPermisos.thunks";
import Loader from "../UI/Loader";
import GrupoPermisosModal from "./GrupoPermisosModal";
import UsuarioPermisosModal from "./UsuarioPermisosModal";

const ROL_LABELS = {
  "ADMIN SYS": { label: "Admin Sistema", color: "bg-danger-100 text-danger-600" },
  ADMIN_ESCOLAR: { label: "Admin Escolar", color: "bg-warning-100 text-warning-600" },
  MENTOR: { label: "Mentor", color: "bg-success-100 text-success-600" },
  ESTUDIANTE: { label: "Estudiante", color: "bg-primary-100 text-primary-600" },
};

const RolesPermisosLayer = () => {
  const dispatch = useDispatch();
  const { grupos, gruposLoading, usuarios, usuariosLoading } = useSelector(
    (s) => s.rolesPermisos
  );

  const [activeTab, setActiveTab] = useState("grupos");
  const [grupoModal, setGrupoModal] = useState(null);
  const [usuarioModal, setUsuarioModal] = useState(null);
  const [userSearch, setUserSearch] = useState("");

  useEffect(() => {
    dispatch(getGruposThunk());
  }, [dispatch]);

  useEffect(() => {
    if (activeTab === "usuarios") {
      dispatch(getUsuariosPermisosThunk(userSearch));
    }
  }, [dispatch, activeTab]);

  const handleUserSearch = (e) => {
    e.preventDefault();
    dispatch(getUsuariosPermisosThunk(userSearch));
  };

  return (
    <div className="card h-100 p-0 radius-12">
      <div className="card-body p-24">
        {/* Header */}
        <div className="d-flex align-items-center justify-content-between mb-4">
          <div>
            <h4 className="fw-semibold mb-1">Roles y Permisos</h4>
            <p className="text-muted text-sm mb-0">
              Gestiona los permisos de los grupos del sistema y los permisos especiales por usuario.
            </p>
          </div>
        </div>

        {/* Tabs */}
        <ul className="nav nav-tabs mb-4">
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === "grupos" ? "active fw-semibold" : ""}`}
              onClick={() => setActiveTab("grupos")}
            >
              <Icon icon="mdi:account-group-outline" className="me-1" />
              Grupos del sistema
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === "usuarios" ? "active fw-semibold" : ""}`}
              onClick={() => setActiveTab("usuarios")}
            >
              <Icon icon="mdi:account-key-outline" className="me-1" />
              Permisos por usuario
            </button>
          </li>
        </ul>

        {/* ── TAB: GRUPOS ──────────────────────────────────────────────── */}
        {activeTab === "grupos" && (
          <>
            {gruposLoading ? (
              <Loader center text="Cargando grupos..." delay={0} />
            ) : (
              <>
                <div className="alert alert-info py-2 mb-3 d-flex align-items-center gap-2">
                  <Icon icon="mdi:information-outline" />
                  <span className="text-sm">
                    Los 3 grupos base (<strong>Administrador del Sistema</strong>, <strong>Admin Escolar</strong>, <strong>Mentor</strong>) no pueden eliminarse.
                    Haz clic en <strong>Editar permisos</strong> para modificar los permisos de cada grupo.
                  </span>
                </div>

                <div className="row g-3">
                  {grupos.map((grupo) => {
                    const isBase = [
                      "Administrador del Sistema",
                      "Admin Escolar",
                      "Mentor",
                    ].includes(grupo.name);

                    return (
                      <div key={grupo.id} className="col-12 col-md-6 col-lg-4">
                        <div
                          className="card border h-100"
                          style={{
                            borderColor: isBase ? "#7749F8" : "#e5e7eb",
                            borderWidth: isBase ? "2px" : "1px",
                          }}
                        >
                          <div className="card-body d-flex flex-column gap-2 p-3">
                            <div className="d-flex align-items-center justify-content-between">
                              <div className="fw-semibold">{grupo.name}</div>
                              {isBase && (
                                <span className="badge bg-primary-100 text-primary-600" style={{ fontSize: "0.65rem" }}>
                                  Base
                                </span>
                              )}
                            </div>
                            <div className="text-muted text-sm">
                              <Icon icon="mdi:shield-check-outline" className="me-1" />
                              {grupo.total_permisos} permisos asignados
                            </div>
                            <button
                              className="btn btn-outline-primary btn-sm mt-auto"
                              onClick={() => setGrupoModal(grupo)}
                            >
                              <Icon icon="mdi:pencil-outline" className="me-1" />
                              Editar permisos
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </>
        )}

        {/* ── TAB: USUARIOS ────────────────────────────────────────────── */}
        {activeTab === "usuarios" && (
          <>
            <form onSubmit={handleUserSearch} className="d-flex gap-2 mb-3">
              <input
                className="form-control"
                placeholder="Buscar por nombre, email o rol..."
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
              />
              <button type="submit" className="btn btn-primary px-4">
                <Icon icon="mdi:magnify" />
              </button>
            </form>

            {usuariosLoading ? (
              <Loader center text="Cargando usuarios..." delay={0} />
            ) : (
              <div className="table-responsive">
                <table className="table table-hover align-middle">
                  <thead>
                    <tr>
                      <th>Usuario</th>
                      <th>Rol</th>
                      <th>Grupos asignados</th>
                      <th>Permisos especiales</th>
                      <th className="text-center">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {usuarios.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="text-center text-muted py-4">
                          No se encontraron usuarios.
                        </td>
                      </tr>
                    ) : (
                      usuarios.map((u) => {
                        const rolInfo = ROL_LABELS[u.rol] ?? {
                          label: u.rol,
                          color: "bg-neutral-200 text-neutral-600",
                        };
                        return (
                          <tr key={u.id}>
                            <td>
                              <div className="fw-semibold">
                                {u.nombres
                                  ? `${u.nombres} ${u.apellido_paterno ?? ""}`.trim()
                                  : u.username}
                              </div>
                              <div className="text-muted text-sm">{u.email}</div>
                            </td>
                            <td>
                              <span className={`badge ${rolInfo.color}`}>
                                {rolInfo.label}
                              </span>
                            </td>
                            <td>
                              <div className="d-flex flex-wrap gap-1">
                                {u.grupos?.length > 0 ? (
                                  u.grupos.map((g) => (
                                    <span
                                      key={g.id}
                                      className="badge bg-primary-100 text-primary-600"
                                    >
                                      {g.name}
                                    </span>
                                  ))
                                ) : (
                                  <span className="text-muted text-sm">Sin grupos</span>
                                )}
                              </div>
                            </td>
                            <td>
                              <span className="badge bg-neutral-200 text-neutral-600">
                                {u.permisos_especiales?.length ?? 0} permisos
                              </span>
                            </td>
                            <td className="text-center">
                              <button
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => setUsuarioModal(u)}
                              >
                                <Icon icon="mdi:pencil-outline" className="me-1" />
                                Gestionar
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modals */}
      {grupoModal && (
        <GrupoPermisosModal
          grupo={grupoModal}
          onClose={() => {
            setGrupoModal(null);
            dispatch(getGruposThunk());
          }}
        />
      )}
      {usuarioModal && (
        <UsuarioPermisosModal
          usuario={usuarioModal}
          onClose={() => {
            setUsuarioModal(null);
            dispatch(getUsuariosPermisosThunk(userSearch));
          }}
        />
      )}
    </div>
  );
};

export default RolesPermisosLayer;
